/**
 * @file UserSwitchMenu.jsx
 * @location src/components/layout/topbar/UserSwitchMenu.jsx
 * @role Topbar 아바타 + 유저 메뉴 (프로필 / 로그아웃). 외부 클릭 시 닫힘.
 *
 * 로그아웃은 SupabaseGate 가 주입하는 auth.signOut 으로 실제 세션 종료.
 * 개발용 유저 전환(uid 스왑)은 import.meta.env.DEV 에서만 노출(QA 편의).
 */
import { useState, useEffect, useRef } from 'react';
import { useApp } from '../../../store/AppContext';
import { kubunLabel, getInitials } from '../../../utils/memberUtils';
import { isManager } from '../../../utils/permissions';
import { Icon } from '../../ui/Icon';

export function UserSwitchMenu() {
  const { S, act, auth } = useApp();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const currentUser = S.members.find(m => m.id === S.uid);
  const isMgr = isManager(currentUser);
  const isDev = import.meta.env.DEV;

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // 로그아웃 — Supabase 세션 종료. 종료 후 SupabaseGate 가 LoginScreen 으로 전환.
  const handleLogout = () => {
    setOpen(false);
    auth?.signOut?.();
  };

  return (
    <div style={{ position: 'relative' }} ref={menuRef}>
      <button className="avbtn" onClick={() => setOpen(v => !v)} aria-label="사용자 메뉴">
        {getInitials(currentUser?.name ?? '')}
      </button>
      {open && (
        <div className="umenu">
          {/* 현재 사용자 헤더 */}
          <div className="umenu-head">
            <span className={`umav ${currentUser?.isManager ? 'bucho' : 'staff'}`}>
              {getInitials(currentUser?.name ?? '')}
            </span>
            <span className="umenu-head-name">
              <strong>{currentUser?.name ?? ''}</strong>
              <span className="umrole">{kubunLabel(currentUser?.isManager)}</span>
            </span>
          </div>

          <button className="umi" onClick={() => { act('SET_NAV', { nav: 'mypage' }); setOpen(false); }}>
            <Icon name="user" size={16} />
            <span style={{ flex: 1, textAlign: 'left' }}>마이 페이지</span>
          </button>

          {/* 설정 = 워크스페이스 운영(구독·데이터·초기화) — 매니저 전용. 모달로 열림 */}
          {isMgr && (
            <button className="umi" onClick={() => { act('SET_UI', { patch: { modalSettings: true } }); setOpen(false); }}>
              <Icon name="settings" size={16} />
              <span style={{ flex: 1, textAlign: 'left' }}>설정</span>
            </button>
          )}

          <button className="umi" onClick={handleLogout}>
            <Icon name="logout" size={16} />
            <span style={{ flex: 1, textAlign: 'left' }}>로그아웃</span>
          </button>

          {/* 개발용 유저 전환 — DEV 빌드에서만 (QA: 매니저/팀원 뷰 확인) */}
          {isDev && (
            <>
              <div className="uml">사용자 전환 (DEV)</div>
              {S.members.filter(m => !m.pending).map(m => (
                <button
                  key={m.id}
                  className={`umi${m.id === S.uid ? ' cur' : ''}`}
                  onClick={() => { act('SET_USER', { id: m.id }); setOpen(false); }}
                >
                  <span className={`umav ${m.isManager ? 'bucho' : 'staff'}`}>{getInitials(m.name)}</span>
                  <span style={{ flex: 1, textAlign: 'left' }}>{m.name}</span>
                  <span className="umrole">{kubunLabel(m.isManager)}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
